import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useKPIGetBookedToChangesHook,
  useKPIGetCancellationsByReasonHook,
  useKPIGetCustomerSatisfactionByCaseTypeHook,
  useKPIGetDeliveredCarsHook,
  useKPIGetInternalDeliveryTransportMixHook,
  useKPIGetResourceUtilizationHook,
  useKPIGetTransportLeadTimeHook,
  useKPIGetTruckCoverageHook,
} from 'api/kpi/kpi';
import {
  GroupingLevel,
  KPIBookedToChangesResult,
  KPICancellationReasonResult,
  KPICustomerSatisfactionResult,
  KPIDeliveredCarsResult,
  KPIInternalDeliveryTransportMixResult,
  KPIResourceUtilizationResult,
  KPITransportLeadTimeResult,
  KPITruckCoverageResult,
} from 'api/model';

/** Riddermark. Rapporten handlar om deras volym, inte om Zäkras totala. */
const RIDDERMARK_SUPPLIER_ID = 1;
const EXCLUDE_COMPANY_NAME = 'Zäkra';

export interface ComparisonPeriod {
  /** Läses som "mot <label>" i rapporten, så den är skriven i den formen. */
  label: string;
  start: Date;
  end: Date;
}

export interface ReportPeriod {
  label: string;
  start: Date;
  end: Date;
  groupBy: GroupingLevel;
  /**
   * Perioden talen jämförs mot. Den ligger på perioden i stället för att räknas fram ur
   * start och slut, eftersom rätt jämförelse inte är densamma för alla: en vecka jämförs med
   * veckan innan, men ett år till dags dato jämförs med samma del av föregående år — inte med
   * de föregående åtta månaderna, som skulle vara höst mot vår.
   */
  comparison: ComparisonPeriod;
  /**
   * Om rapporten också bryts ned per månad. Bara året till dags dato gör det: där jämförs
   * varje månad med samma månad i fjol, och den jämförelsen finns inte för ett fritt spann —
   * dess jämförelseperiod ligger omedelbart före, så "samma månad" blir en annan månad.
   */
  monthly: boolean;
}

/**
 * Senast avslutade rapportvecka: fredag 00:00 till och med torsdag 23:59.
 * Dras rapporten på fredagen är det veckan som just tog slut, och veckorna ligger kant i kant
 * utan överlapp — samma definition som filterraden i RM-nyckeltal använder.
 */
const reportWeek = (weeksBack = 0): [Date, Date] => {
  const now = new Date();
  const daysSinceFriday = (now.getDay() - 5 + 7) % 7;
  const end = new Date(now);
  end.setDate(now.getDate() - daysSinceFriday - weeksBack * 7);
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(end.getDate() - 7);
  return [start, new Date(end.getTime() - 1000)];
};

const yearToDate = (): [Date, Date] => {
  const now = new Date();
  return [new Date(now.getFullYear(), 0, 1), now];
};

/**
 * Samma del av föregående år: 1 jan → samma dag i fjol.
 *
 * Datumet byggs av delar i stället för med `setFullYear`. Den senare spiller över på skottdagen
 * — 29 februari minus ett år blir 1 mars, och då är jämförelseperioden ett dygn för lång var
 * fjärde år. Dagen klipps i stället till månadens sista dag i målåret.
 */
const yearToDateLastYear = (): [Date, Date] => {
  const now = new Date();
  const year = now.getFullYear() - 1;
  const month = now.getMonth();
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
  const day = Math.min(now.getDate(), lastDayOfMonth);
  return [
    new Date(year, 0, 1),
    new Date(year, month, day, now.getHours(), now.getMinutes(), now.getSeconds()),
  ];
};

/**
 * Vad användaren har valt. Perioden räknas fram ur det här i stället för att lagras direkt,
 * eftersom en vald vecka måste kunna stegas — och att stega ett datumintervall som redan är
 * uträknat betyder att räkna baklänges ur ett resultat i stället för framlänges ur ett val.
 */
export type PeriodSelection =
  | { kind: 'week'; weeksBack: number }
  | { kind: 'yearToDate' }
  | { kind: 'custom'; start: Date; end: Date };

const weekLabel = (weeksBack: number) => {
  if (weeksBack === 0) return 'Rapportvecka';
  // Etiketten på knappen "Föregående" behålls för veckan innan, så att rapporten som redan
  // mailats den vägen fortsätter heta samma sak i dokumenthuvudet.
  if (weeksBack === 1) return 'Föregående';
  return `Rapportvecka −${weeksBack}`;
};

const weekComparisonLabel = (weeksBack: number) => {
  if (weeksBack === 0) return 'föregående vecka';
  if (weeksBack === 1) return 'veckan dessförinnan';
  return 'veckan innan';
};

const weekPeriod = (weeksBack: number): ReportPeriod => {
  const [start, end] = reportWeek(weeksBack);
  const [prevStart, prevEnd] = reportWeek(weeksBack + 1);
  return {
    label: weekLabel(weeksBack),
    start,
    end,
    groupBy: GroupingLevel.Day,
    monthly: false,
    comparison: {
      label: weekComparisonLabel(weeksBack),
      start: prevStart,
      end: prevEnd,
    },
  };
};

const yearToDatePeriod = (): ReportPeriod => {
  const [start, end] = yearToDate();
  const [lastYearStart, lastYearEnd] = yearToDateLastYear();
  return {
    label: 'År 1 jan → nu',
    start,
    end,
    // Per månad och inte per vecka: månadstabellen räknas ur datapunkterna, och panelerna slår
    // ändå ihop datumen. Excel-underlagets periodkolumn blir därmed månader också.
    groupBy: GroupingLevel.Month,
    monthly: true,
    comparison: { label: 'samma period i fjol', start: lastYearStart, end: lastYearEnd },
  };
};

/** Hur många av månadernas ledtider som hämtas samtidigt, per år. */
const LEAD_TIME_CONCURRENCY = 3;

/** Ett dygn i millisekunder — spannets längd räknas i dygn, inte i månader. */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Ett fritt valt spann.
 *
 * Jämförelsen blir den lika långa perioden omedelbart före. Det är det enda svaret som håller
 * för ett godtyckligt spann: "föregående månad" går inte att definiera för 10 maj → 3 juni, och
 * att jämföra mot något som inte är lika långt gör skillnaden till en skillnad i hur många dygn
 * som mättes. Slutet backas en sekund så att perioderna ligger kant i kant utan överlapp.
 *
 * Grupperingen följer spannets längd: dagsstaplar blir oläsliga bortom en månad. Tröskeln är
 * satt här och har ingen motsvarighet på andra håll — RM-nyckeltal låter användaren välja
 * gruppering i en egen meny i stället för att byta automatiskt.
 */
const customPeriod = (start: Date, end: Date): ReportPeriod => {
  // Längden räknas i hela dygn, inte i millisekunder. Slutet ligger på 23:59:59.999, så en ren
  // millisekundsubtraktion lägger jämförelsens start en sekund före midnatt — den visas då som
  // dygnet innan det den är, och drar med sig den sista sekunden av det dygnet in i talen.
  const days = Math.max(1, Math.round((end.getTime() - start.getTime() + 1) / DAY_MS));

  // setDate och inte en fast multiplikation med 24 timmar: över en sommartidsomställning är ett
  // dygn 23 eller 25 timmar, och då glider jämförelseperioden en timme ur fas med perioden.
  const comparisonStart = new Date(start);
  comparisonStart.setDate(start.getDate() - days);
  comparisonStart.setHours(0, 0, 0, 0);

  // Slutar precis där perioden börjar, så de ligger kant i kant utan överlapp.
  const comparisonEnd = new Date(start.getTime() - 1);

  return {
    label: 'Valt spann',
    start,
    end,
    groupBy: days > 31 ? GroupingLevel.Week : GroupingLevel.Day,
    monthly: false,
    comparison: {
      label: 'lika lång period dessförinnan',
      start: comparisonStart,
      end: comparisonEnd,
    },
  };
};

/** Perioden ett val motsvarar. */
export const periodOf = (selection: PeriodSelection): ReportPeriod => {
  switch (selection.kind) {
    case 'week':
      return weekPeriod(selection.weeksBack);
    case 'yearToDate':
      return yearToDatePeriod();
    default:
      return customPeriod(selection.start, selection.end);
  }
};

/**
 * Hur långt bak rapportveckorna går att stega.
 *
 * Gränsen finns för att stoppa en promenad ut ur det underlag som finns: ärenden dateras på
 * stängningstidpunkten, och tillräckligt långt bak blir varje panel tom utan att säga varför.
 * Ett år räcker för den jämförelse ledningsgruppen gör.
 */
export const MAX_WEEKS_BACK = 52;

/** Snabbvalen i verktygsraden, i den ordning de visas. */
export const SHORTCUTS: { label: string; selection: PeriodSelection }[] = [
  { label: 'Rapportvecka', selection: { kind: 'week', weeksBack: 0 } },
  { label: 'Föregående', selection: { kind: 'week', weeksBack: 1 } },
  { label: 'År 1 jan → nu', selection: { kind: 'yearToDate' } },
];

const MONTH_LABELS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** En kalendermånad inom perioden, klippt till periodens ändar. */
export interface MonthSlot {
  /** Samma nyckel som backend grupperar på: år och månad i svensk tid. */
  year: number;
  month: number;
  label: string;
  start: Date;
  end: Date;
  /** Månaden är inte slut — perioden slutar innan den gör det. */
  partial: boolean;
}

/**
 * Kalendermånaderna perioden täcker, i ordning.
 *
 * Den sista klipps vid periodens slut och märks som påbörjad. Den jämförs ändå rakt av mot
 * samma månad i fjol, eftersom jämförelseperioden slutar på samma dag i fjol och fjolårets
 * månad därmed är klippt på samma ställe.
 */
export const monthsOf = (start: Date, end: Date): MonthSlot[] => {
  const slots: MonthSlot[] = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    const next = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    const monthEnd = new Date(next.getTime() - 1);
    slots.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth(),
      label: MONTH_LABELS[cursor.getMonth()],
      start: cursor < start ? start : cursor,
      end: monthEnd > end ? end : monthEnd,
      partial: monthEnd > end,
    });
    cursor = next;
  }
  return slots;
};

/**
 * P9 per månad, en hämtning per månad.
 *
 * Övriga mått räknas ur periodens månadsgrupperade datapunkter, men ledtiden redovisas som
 * median och medianer går inte att väga ihop — varken ur länsraderna eller ur något annat.
 * Varje månad måste därför räknas på sina egna bilar, precis som året räknas på sina.
 */
export interface MonthlyLeadTime {
  /** Null när månaderna inte gick att hämta; då står P9 med streck i månadsvyn. */
  current: KPITransportLeadTimeResult[] | null;
  /** Null när jämförelsen inte gick att hämta; då tonas inte månadernas ledtider. */
  previous: KPITransportLeadTimeResult[] | null;
}

export interface ReportData {
  deliveredCars: KPIDeliveredCarsResult;
  transportMix: KPIInternalDeliveryTransportMixResult;
  cancellations: KPICancellationReasonResult;
  bookedToChanges: KPIBookedToChangesResult;
  satisfaction: KPICustomerSatisfactionResult;
  utilization: KPIResourceUtilizationResult;
  truckCoverage: KPITruckCoverageResult;
  leadTime: KPITransportLeadTimeResult;
}

/**
 * Hämtar hela rapporten för en period, och samma rapport för jämförelseperioden.
 *
 * Alla anropen går i ett svep med samma filter. Skälet är detsamma som att Excel-filen
 * byggs av en enda hämtning i backend: hämtas panelerna var för sig kan två av dem hamna på
 * olika sida om ett godkännande, och då stämmer inte talen mot varandra på en sida som är
 * tänkt att skrivas ut och skickas vidare.
 *
 * Jämförelsen hämtas i samma svep och inte efteråt. Sidan ska kunna skrivas ut i samma ögonblick
 * som den slutat ladda, och en jämförelse som kommer in efter utskriften finns inte på pappret.
 * Anropen går parallellt, så det kostar knappt något i väggklocka.
 */
const useReportData = (period: ReportPeriod) => {
  const getDeliveredCars = useKPIGetDeliveredCarsHook();
  const getTransportMix = useKPIGetInternalDeliveryTransportMixHook();
  const getCancellations = useKPIGetCancellationsByReasonHook();
  const getBookedToChanges = useKPIGetBookedToChangesHook();
  const getSatisfaction = useKPIGetCustomerSatisfactionByCaseTypeHook();
  const getUtilization = useKPIGetResourceUtilizationHook();
  const getTruckCoverage = useKPIGetTruckCoverageHook();
  const getLeadTime = useKPIGetTransportLeadTimeHook();

  const [data, setData] = useState<ReportData | null>(null);
  const [previous, setPrevious] = useState<ReportData | null>(null);
  const [monthlyLeadTime, setMonthlyLeadTime] = useState<MonthlyLeadTime | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const filter = useMemo(
    () => ({
      startDate: period.start,
      endDate: period.end,
      supplierIDs: [RIDDERMARK_SUPPLIER_ID],
      excludeCompanyNameContains: EXCLUDE_COMPANY_NAME,
      groupBy: period.groupBy,
    }),
    [period.start, period.end, period.groupBy]
  );

  // Jämförelseperioden hämtas med samma filter så att skillnaden blir en skillnad i
  // verksamheten och inte i urvalet.
  const comparisonFilter = useMemo(
    () => ({
      ...filter,
      startDate: period.comparison.start,
      endDate: period.comparison.end,
    }),
    [filter, period.comparison.start, period.comparison.end]
  );

  const fetchAll = useCallback(
    async (f: typeof filter, withCancellationCases: boolean): Promise<ReportData> => {
      // P9 hämtas i en enda grupp, inte per dag som resten. Panelen slår ändå ihop datumen
      // igen — den visar län, inte dagar — och medianer går inte att väga ihop till en median.
      // Ett viktat snitt av dagsmedianer är varken det ena eller det andra, och nu när
      // rapporten redovisar medianen som huvudtal är det talet läsaren går på. Årsgruppering
      // är den grövsta backend har och ger en grupp för både rapportveckan och året till dags
      // dato; undantaget är en rapportvecka som korsar ett årsskifte, då det blir två.
      const leadTimeFilter = { ...f, groupBy: GroupingLevel.Year };

      // Bilarna bakom varje avbokningsorsak hämtas bara hit. Flaggan sitter på anropet och inte
      // i det delade filtret, eftersom samma filter också skickas till Word-uttaget — som inte
      // ritar listorna och inte ska räkna fram dem. Jämförelseperioden hämtar dem inte heller:
      // panelen fäller bara ut den aktuella periodens bilar, och "År 1 jan → nu" jämförs mot
      // ett helt fjolår.
      const cancellationFilter = { ...f, includeCancellationCases: withCancellationCases };
      // Samma sak för bilarna bakom varje orsak till ändrad bokningstid.
      const bookedToChangeFilter = { ...f, includeBookedToChangeCases: withCancellationCases };

      const [
        deliveredCars,
        transportMix,
        cancellations,
        bookedToChanges,
        satisfaction,
        utilization,
        truckCoverage,
        leadTime,
      ] = await Promise.all([
        getDeliveredCars(f),
        getTransportMix(f),
        getCancellations(cancellationFilter),
        getBookedToChanges(bookedToChangeFilter),
        getSatisfaction(f),
        getUtilization(f),
        getTruckCoverage(f),
        getLeadTime(leadTimeFilter),
      ]);
      return {
        deliveredCars,
        transportMix,
        cancellations,
        bookedToChanges,
        satisfaction,
        utilization,
        truckCoverage,
        leadTime,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Månadernas ledtider, några i taget.
   *
   * Alla på en gång blev upp till 24 anrop ovanpå rapportens övriga sexton, och i den
   * klungan dog månadsanropen ibland samtidigt utan svar. Ett tak på tre samtidiga per år
   * kostar några sekunder men tar bort klungan.
   */
  const fetchMonthlyLeadTime = useCallback(
    async (f: typeof filter, months: MonthSlot[]) => {
      const results: KPITransportLeadTimeResult[] = [];
      for (let i = 0; i < months.length; i += LEAD_TIME_CONCURRENCY) {
        // eslint-disable-next-line no-await-in-loop
        const batch = await Promise.all(
          months
            .slice(i, i + LEAD_TIME_CONCURRENCY)
            .map((m) =>
              getLeadTime({ ...f, startDate: m.start, endDate: m.end, groupBy: GroupingLevel.Year })
            )
        );
        results.push(...batch);
      }
      return results;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Vilken hämtning som är den senaste.
   *
   * Byter användaren period medan den förra fortfarande laddar kommer svaren i den ordning
   * backend blir klar, inte i den ordning de begärdes. En rapportvecka vars långsammaste anrop
   * kommer in efter årets skrev annars sina tal under årets rubrik. Bara den senaste hämtningen
   * får skriva till sidan; de äldre svaren kastas.
   */
  const latestLoad = useRef(0);

  const load = useCallback(async () => {
    latestLoad.current += 1;
    const loadId = latestLoad.current;
    const isStale = () => loadId !== latestLoad.current;
    setIsLoading(true);
    setError(null);
    try {
      // Månadernas ledtider hämtas i samma svep som resten, av samma skäl som jämförelsen: det
      // som kommer in efter utskriften finns inte på pappret.
      const months = period.monthly ? monthsOf(period.start, period.end) : [];
      const comparisonMonths = period.monthly
        ? monthsOf(period.comparison.start, period.comparison.end)
        : [];

      // Perioden och jämförelsen hämtas ihop. Misslyckas jämförelsen ska rapporten ändå gå att
      // läsa, så den felar inte hela sidan — då visas talen utan trend i stället.
      const [current, comparison, leadCurrent, leadPrevious] = await Promise.all([
        fetchAll(filter, true),
        fetchAll(comparisonFilter, false).catch(() => null),
        // Månadernas ledtider fäller inte rapporten. Året och de övriga måtten står sig utan dem;
        // saknas de blir P9:s månadsrad streck i stället för att hela sidan blir tom.
        fetchMonthlyLeadTime(filter, months).catch(() => null),
        fetchMonthlyLeadTime(comparisonFilter, comparisonMonths).catch(() => null),
      ]);
      if (isStale()) return;
      setData(current);
      setPrevious(comparison);
      setMonthlyLeadTime(
        period.monthly ? { current: leadCurrent, previous: comparison ? leadPrevious : null } : null
      );
    } catch (e) {
      if (isStale()) return;
      // Nolla talen, inte bara flagga felet. Annars står föregående periods siffror kvar under
      // den nya periodens rubrik och jämförelseetikett — veckotal presenterade som "samma
      // period i fjol", vilket är värre än en tom sida.
      setData(null);
      setPrevious(null);
      setMonthlyLeadTime(null);
      setError(e);
    } finally {
      if (!isStale()) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, comparisonFilter, period.monthly]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, previous, monthlyLeadTime, isLoading, error, filter, reload: load };
};

export default useReportData;
